import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { app, db } from "../firebase";
import { useAuth } from "./AuthContext";

const UserContext = createContext();
export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
    const { currentUser, ready } = useAuth();
    const [userData, setUserData] = useState(null);
    const [userLoading, setUserLoading] = useState(true);
    const [businessLoading, setBusinessLoading] = useState(false);
    const [signupLoading, setSignupLoading] = useState(false);
    const [retryTrigger, setRetryTrigger] = useState(0);

    const fetchUserData = useCallback(async () => {
        if (!ready) return;

        if (!currentUser?.uid) {
            setUserData(null);
            setUserLoading(false);
            setSignupLoading(false);
            return;
        }

        setUserLoading(true);

        try {
            const userRef = doc(db, "users", currentUser.uid);
            const userSnap = await getDoc(userRef);

            if (!userSnap.exists()) {
                setSignupLoading(true);
                setTimeout(() => setRetryTrigger(prev => prev + 1), 500);
                return;
            }

            setSignupLoading(false);

            const firestoreData = userSnap.data();
            const {
                name,
                tel,
                role,
                businessCode,
                createdAt: userCreatedAt,
                joinedAt: userJoinedAt,
                profile_image
            } = firestoreData;

            const hydratedUser = {
                uid: currentUser.uid,
                name: name || currentUser.displayName || "",
                email: currentUser.email,
                photoURL: currentUser.photoURL || profile_image || null,
                tel: tel || "",
                role: role || "user",
                businessCode: businessCode || null,
                businessId: null,
                userCreatedAt: userCreatedAt || null,
                userJoinedAt: userJoinedAt || null,
                businessCreatedAt: null,
                businessInfo: null
            };

            // Preserve business snapshot across refresh so soft-lock isn't
            // briefly bypassed while businessInfo reloads
            setUserData(prev => {
                if (prev?.businessCode === hydratedUser.businessCode && prev?.businessInfo) {
                    return {
                        ...hydratedUser,
                        businessId: prev.businessId,
                        businessCreatedAt: prev.businessCreatedAt,
                        businessCurrency: prev.businessCurrency,
                        businessInfo: prev.businessInfo
                    };
                }
                return hydratedUser;
            });
        } catch (error) {
            setSignupLoading(false);
        } finally {
            setUserLoading(false);
        }
    }, [currentUser, ready]);

    useEffect(() => {
        const fetchBusinessInfo = async () => {
            if (!userData?.businessCode || businessLoading) return;

            setBusinessLoading(true);

            try {
                const businessQuery = query(
                    collection(db, "businesses"),
                    where("businessCode", "==", userData.businessCode)
                );
                const businessSnap = await getDocs(businessQuery);

                if (!businessSnap.empty) {
                    const businessDoc = businessSnap.docs[0];
                    const businessData = businessDoc.data();
                    const businessCreatedAt = businessData.createdAt || null;
                    const businessCurrency = businessData.businessCurrency || "ZAR"; // fallback if missing

                    setUserData(prev => ({
                        ...prev,
                        businessId: businessDoc.id,
                        businessCreatedAt,
                        businessCurrency,
                        businessInfo: businessData
                    }));

                }
            } catch (error) {
                // silently fail
            } finally {
                setBusinessLoading(false);
            }
        };

        if (userData?.businessCode) fetchBusinessInfo();
        // Re-fetch when retryTrigger changes (refreshUser) so subscription status updates
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData?.businessCode, retryTrigger]);

    // Set custom claims (businessId) for Storage rules; refresh token so it takes effect
    useEffect(() => {
        if (!currentUser || !userData?.businessId) return;
        const refreshClaims = async () => {
            try {
                const fn = httpsCallable(getFunctions(app), "refreshCustomClaims");
                await fn();
                await currentUser.getIdToken(true);
            } catch (e) {
                // Non-fatal: Storage downloads may still work for legacy paths
                console.warn("Refresh custom claims:", e);
            }
        };
        refreshClaims();
    }, [currentUser, userData?.businessId]);

    useEffect(() => {
        if (!ready) return;
        fetchUserData();
    }, [fetchUserData, currentUser?.uid, retryTrigger, ready]);

    const loading = userLoading || businessLoading;
    const hydrated = !!userData?.businessId && !!userData?.businessInfo;

    const clearUserData = () => {
        setUserData(null);
        setUserLoading(false);
        setBusinessLoading(false);
        setSignupLoading(false);
    };

    return (
        <UserContext.Provider
            value={{
                userData,
                loading,
                hydrated,
                signupLoading,
                refreshUser: () => setRetryTrigger(prev => prev + 1),
                clearUserData
            }}
        >
            {children}
        </UserContext.Provider>
    );
};
